<template>
  <v-app-bar elevation="0" height="72" class="px-2 top-bar"
    :class="{ 'top-bar--form-view': isFormView, 'border-b-sm': !isFormView }" color="surface">
    <template #prepend v-if="!isFormView">
      <!-- Mobile: drawer toggle -->
      <v-btn v-if="$vuetify.display.smAndDown" icon variant="flat" rounded="lg" size="40" class="nav-icon"
        @click="$emit('toggle-drawer')">
        <v-icon size="20">mdi-menu</v-icon>
      </v-btn>
      <!-- Desktop: rail toggle -->
      <v-btn v-else icon variant="flat" rounded="lg" size="40" class="nav-icon" @click="$emit('toggle-rail')">
        <v-icon size="20">{{ rail ? 'mdi-menu-close' : 'mdi-menu-open' }}</v-icon>
      </v-btn>
    </template>

    <v-spacer />

    <v-menu location="bottom end" offset="8" class="cursor-pointer">
      <template #activator="{ props }">
        <v-badge floating location="top right" :offset-x="11" :offset-y="11"
          :color="hasNotifications ? 'accent-color' : 'success-color'" :model-value="true" dot
          class="mr-3 notification-badge"
          :class="{ 'notification-badge--active': hasNotifications }">
          <v-btn v-bind="props" icon variant="flat" rounded="lg" size="34" class="nav-icon cursor-pointer">
            <v-icon size="18">mdi-bell-outline</v-icon>
          </v-btn>
        </v-badge>
      </template>
      <v-card class="notification-menu" elevation="0" rounded="lg" width="400" >
        <v-card-title class="text-subtitle-1 font-weight-bold pb-2">Notifications</v-card-title>
        <v-divider />
        <v-card-text class="pt-4" v-if="hasNotifications">
          <v-row v-if="showFreeTrial">
            <v-col cols="12" class="d-flex align-center">
              <p class="text-body-medium">
                <strong class="text-accent-color">{{ userMixin_trialDaysLeft }} out of {{ plan.trial_days }}
                  days</strong>
                left
                on the trial.
                <router-link :to="{ name: 'plans.index' }" class="text-accent-color">Upgrade</router-link>
                for the full experience!
              </p>
            </v-col>
          </v-row>
          <v-divider class="py-1" v-if="showEmailVerification" />
          <v-row v-if="showEmailVerification">
            <v-col cols="12" class="d-flex align-center flex-column">
              <div>
                👋 Hey <strong class="text-accent-color">{{ user?.name }}</strong>! We need you to confirm your email
                address.
              </div>
              <div>
                <v-btn size="small" variant="outlined" color="brand-color" class="ml-2 text-none"
                  @click="sendConfirmation">
                  Confirm Email
                </v-btn>
                <v-btn size="small" variant="flat" color="accent-color" class="ml-2 text-none" @click="remindLater">
                  Remind Me Later
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text class="pt-4" v-else>
          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <p class="text-body-medium">
                No new notifications.
              </p>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-menu>

    <UserMenu :user-data="userDetails" :minimal="isFormView" />
  </v-app-bar>
</template>

<script>
import UserMenu from './UserMenu.vue'
import userMixin from '@/mixins/user'
import * as actionTypes from '@/store/action-types'

export default {
  name: 'TopNav',
  components: {
    UserMenu
  },
  mixins: [userMixin],
  computed: {
    showEmailVerification() {
      return this.$store.getters['notification/showVerifyEmail'] && !this.user?.verified
    },
    showFreeTrial() {
      return !this.isAdmin &&
        !this.isSuperCustomer &&
        this.userMixin_hasActiveFreeTrialPlan &&
        !this.userMixin_planExpired
    },
    hasNotifications() {
      return this.showEmailVerification || this.showFreeTrial
    },
    userDetails() {
      return this.user
    }
  },
  props: {
    rail: { type: Boolean, default: false },
    isFormView: { type: Boolean, default: false },
  },
  emits: ['toggle-drawer', 'toggle-rail'],
  methods: {
    sendConfirmation() {
      if (!this.user?.email) {
        return
      }
      this.$store.dispatch(actionTypes.SEND_ACCOUNT_VERIFICATION, {
        email: this.user.email
      }).then(() => {
        this.remindLater()
      })
    },
    remindLater() {
      this.$store.dispatch('notification/toggleVisibility', {
        key: 'verifyEmail',
        value: false
      }).then(() => {
        localStorage.setItem('notification.dashboard.verifyEmailHide', 1, 3 * 60 * 60 * 1000)
      })
    }
  }
}
</script>

<style scoped lang="scss">
.top-bar {
  background: white !important;
  border-bottom: 1px solid rgba($border-color-hex, 0.12);
}

.top-bar--form-view {
  border-bottom: none !important;
  box-shadow: none !important;
  background: var(--background-color) !important;
}

.nav-icon {
  background-color: transparent !important;
  border: 1px solid rgba($brand-color-hex, 0.5) !important;
  color: rgba($brand-color-hex, 0.9) !important;
}

.nav-icon :deep(.v-icon) {
  color: inherit !important;
}

.nav-icon:hover {
  background-color: rgba($brand-color-hex, 0.08) !important;
  border-color: rgba($brand-color-hex, 0.5) !important;
}


@keyframes notification-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba($accent-color-hex, 0.5);
  }

  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 6px rgba($accent-color-hex, 0);
  }

  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba($accent-color-hex, 0);
  }
}

.notification-badge--active :deep(.v-badge__badge) {
  animation: notification-pulse 1.4s ease-in-out infinite;
}

.notification-badge :deep(.v-badge__badge) {
  width: 8px !important;
  height: 8px !important;
  min-width: 8px !important;
  border: 2px solid white !important;
}

</style>
